/**
 * StockSentryLogo Component
 *
 * Brand logo component featuring bold concentric rings with radar sweep lines.
 * Used in dashboard header and other brand-prominent locations.
 *
 * Design: Precision surveillance meets luxury tech - bold rings, multi-sweep radar wake,
 * detection blips, and vibrant purple gradient background.
 */

import { useId } from 'react';

interface StockSentryLogoProps {
  /** Size of the logo in pixels (default: 72) */
  size?: number;
  /** Optional color override for icon-only rendering (no background) */
  color?: string;
  /** Whether to animate the internal elements - triggers pulse on inner circle and ripple patterns */
  animate?: boolean;
}

export function StockSentryLogo({ size = 72, color, animate = false }: StockSentryLogoProps) {
  // Use React's useId for stable unique IDs
  const baseId = useId().replace(/:/g, '');

  // Size-adaptive feature flags
  const showSecondarySwep = size >= 40;
  const showDetectionBlips = size >= 48;
  const showTertiarySwep = size >= 64;

  // If color is provided, render icon-only (no gradient background)
  if (color) {
    return (
      <svg
        // Key forces remount when animate changes, ensuring SMIL animations restart
        key={animate ? `${baseId}-animating` : `${baseId}-static`}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Stock Sentry logo"
      >
        {/* Main rings - bold concentric circles */}
        <g stroke={color} strokeLinecap="round" fill="none">
          <circle cx="12" cy="12" r="10" strokeWidth="2.1" />
          <circle cx="12" cy="12" r="6.2" strokeWidth="1.9" />
          <circle cx="12" cy="12" r="2.8" strokeWidth="0.75" opacity="0.6" />
        </g>

        {/* Tertiary sweep line (furthest trailing) */}
        {showTertiarySwep && (
          <line
            x1="12"
            y1="12"
            x2="17"
            y2="9"
            stroke={color}
            strokeWidth="0.7"
            strokeLinecap="round"
            opacity="0.25"
          />
        )}

        {/* Secondary sweep line (trailing) */}
        {showSecondarySwep && (
          <line
            x1="12"
            y1="12"
            x2="18.5"
            y2="6.5"
            stroke={color}
            strokeWidth="1.0"
            strokeLinecap="round"
            opacity="0.5"
          />
        )}

        {/* Primary radar sweep line */}
        <line
          x1="12"
          y1="12"
          x2="19.5"
          y2="4.5"
          stroke={color}
          strokeWidth="1.6"
          strokeLinecap="round"
        />

        {/* Center dot */}
        <circle cx="12" cy="12" r="1.8" fill={color} />

        {/* Animated elements - only render when animate is true */}
        {animate && (
          <>
            {/* Inner circle pulse - animates radius */}
            <circle cx="12" cy="12" fill={color} r="1.8">
              <animate
                attributeName="r"
                values="1.8;2.8;1.8"
                dur="350ms"
                calcMode="spline"
                keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
              />
              <animate
                attributeName="opacity"
                values="1;0.6;1"
                dur="350ms"
                calcMode="spline"
                keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
              />
            </circle>

            {/* First ripple ring - expands from center */}
            <circle cx="12" cy="12" stroke={color} strokeWidth="1" fill="none" r="1.8" opacity="0.6">
              <animate
                attributeName="r"
                values="1.8;9"
                dur="400ms"
                calcMode="spline"
                keySplines="0.4 0 0.6 1"
              />
              <animate
                attributeName="opacity"
                values="0.6;0"
                dur="400ms"
                calcMode="spline"
                keySplines="0.4 0 0.6 1"
              />
              <animate
                attributeName="stroke-width"
                values="1.5;0.5"
                dur="400ms"
                calcMode="spline"
                keySplines="0.4 0 0.6 1"
              />
            </circle>

            {/* Second ripple ring - delayed, different size */}
            <circle cx="12" cy="12" stroke={color} strokeWidth="1" fill="none" r="1.8" opacity="0">
              <animate
                attributeName="r"
                values="1.8;7"
                dur="350ms"
                begin="80ms"
                calcMode="spline"
                keySplines="0.4 0 0.6 1"
              />
              <animate
                attributeName="opacity"
                values="0.5;0"
                dur="350ms"
                begin="80ms"
                calcMode="spline"
                keySplines="0.4 0 0.6 1"
              />
              <animate
                attributeName="stroke-width"
                values="1;0.3"
                dur="350ms"
                begin="80ms"
                calcMode="spline"
                keySplines="0.4 0 0.6 1"
              />
            </circle>
          </>
        )}
      </svg>
    );
  }

  // Default: full logo with brand purple gradient background and enhanced design
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Stock Sentry logo"
    >
      <defs>
        {/* Primary gradient - rich violet to deep purple */}
        <linearGradient
          id={`${baseId}-gradient`}
          x1="0"
          y1="0"
          x2="48"
          y2="48"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#a78bfa" />
          <stop offset="0.5" stopColor="#8b5cf6" />
          <stop offset="1" stopColor="#7c3aed" />
        </linearGradient>

        {/* Radar sweep glow gradient */}
        <linearGradient
          id={`${baseId}-sweep-glow`}
          x1="24"
          y1="24"
          x2="41.2"
          y2="6.8"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ffffff" stopOpacity="1" />
          <stop offset="0.7" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="1" stopColor="#e0d4ff" stopOpacity="0.7" />
        </linearGradient>

        {/* Secondary sweep glow gradient */}
        <linearGradient
          id={`${baseId}-sweep-glow-secondary`}
          x1="24"
          y1="24"
          x2="38.5"
          y2="12.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0.2" />
        </linearGradient>

        {/* Enhanced sweep arc gradient for radar beam - wider and stronger */}
        <linearGradient
          id={`${baseId}-sweep-arc`}
          x1="24"
          y1="6"
          x2="41"
          y2="24"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ffffff" stopOpacity="0.45" />
          <stop offset="0.4" stopColor="#ffffff" stopOpacity="0.15" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>

        {/* Center glow - larger radius */}
        <radialGradient
          id={`${baseId}-center-glow`}
          cx="24"
          cy="24"
          r="10"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ffffff" stopOpacity="1" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.7" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>

        {/* Glow filter for radar line */}
        <filter id={`${baseId}-line-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="0.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Squircle background */}
      <rect width="48" height="48" rx="11" fill={`url(#${baseId}-gradient)`} />

      {/* Enhanced radar sweep arc - wider 45-degree fan */}
      <path
        d="M 24 24 L 41.2 6.8 A 21.5 21.5 0 0 0 6.8 6.8 L 24 24 Z"
        fill={`url(#${baseId}-sweep-arc)`}
        opacity="0.55"
      />

      {/* Outer ring - BOLDER */}
      <circle
        cx="24"
        cy="24"
        r="21.5"
        stroke="white"
        strokeWidth="4.2"
        fill="none"
        opacity="0.95"
      />

      {/* Middle ring - well-spaced, thicker */}
      <circle
        cx="24"
        cy="24"
        r="16"
        stroke="white"
        strokeWidth="3.8"
        fill="none"
        opacity="0.92"
      />

      {/* Inner ring - more visible */}
      <circle
        cx="24"
        cy="24"
        r="10"
        stroke="white"
        strokeWidth="1.5"
        fill="none"
        opacity="0.65"
      />

      {/* Tertiary sweep line (furthest trailing) - only at larger sizes */}
      {showTertiarySwep && (
        <line
          x1="24"
          y1="24"
          x2="35"
          y2="18"
          stroke="white"
          strokeWidth="1.4"
          strokeLinecap="round"
          opacity="0.25"
        />
      )}

      {/* Secondary sweep line (trailing) */}
      {showSecondarySwep && (
        <line
          x1="24"
          y1="24"
          x2="38.5"
          y2="12.5"
          stroke={`url(#${baseId}-sweep-glow-secondary)`}
          strokeWidth="2.0"
          strokeLinecap="round"
          opacity="0.5"
        />
      )}

      {/* Primary radar sweep line - PROMINENT with glow */}
      <line
        x1="24"
        y1="24"
        x2="41.2"
        y2="6.8"
        stroke={`url(#${baseId}-sweep-glow)`}
        strokeWidth="2.8"
        strokeLinecap="round"
        filter={`url(#${baseId}-line-glow)`}
      />

      {/* Sweep line endpoint dot */}
      <circle cx="41.2" cy="6.8" r="2.0" fill="white" opacity="0.9" />

      {/* Detection blips - small dots suggesting detected items */}
      {showDetectionBlips && (
        <>
          <circle cx="39" cy="11" r="1.8" fill="white" opacity="0.7" />
          <circle cx="36" cy="16" r="1.2" fill="white" opacity="0.4" />
        </>
      )}

      {/* Center glow halo - larger */}
      <circle cx="24" cy="24" r="9" fill={`url(#${baseId}-center-glow)`} />

      {/* Center dot - bold */}
      <circle cx="24" cy="24" r="4.5" fill="white" />
    </svg>
  );
}
